/**
 * Utility to parse structured LLM JSON into clinical HTML for the TipTap editor.
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Format a date-ish value for display in clinical documents.
 *
 * The backend sends dates as ISO timestamps (e.g. "2026-06-03T00:00:00"); we
 * want just the calendar date ("03 Jun 2026"). The date part is read straight
 * from the string (not via `new Date`) so date-only values never shift across
 * timezones. Empty → "-"; anything unparseable is returned unchanged.
 */
export const formatClinicalDate = (val) => {
  if (val === null || val === undefined || val === "") return "-";
  const str = String(val).trim();

  // ISO date or datetime: YYYY-MM-DD[THH:mm:ss...] — format from the string itself.
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, y, m, d] = iso;
    const mon = MONTHS[parseInt(m, 10) - 1];
    if (mon) return `${d} ${mon} ${y}`;
  }

  // Fallback: trust a valid parse, otherwise leave the value as-is.
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    return `${String(parsed.getDate()).padStart(2, "0")} ${MONTHS[parsed.getMonth()]} ${parsed.getFullYear()}`;
  }
  return str;
};

export const parseSummaryJsonToHtml = (jsonPayload) => {
  if (!jsonPayload || typeof jsonPayload !== "object") return "";

  const safeArray = (val) => (Array.isArray(val) ? val.filter(v => v !== null && v !== "") : []);
  const safeStr = (val) => (val !== null && val !== undefined && val !== "" ? String(val) : "-");
  // List items may arrive as plain strings or as objects (e.g. investigations as
  // { orderDetail: "..." }). Extract a readable string so they never render as "[object Object]".
  const itemText = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "object") {
      return String(val.orderDetail || val.detail || val.description || val.name || val.text || val.value || "");
    }
    return String(val);
  };

  let htmlString = `<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">`;
  htmlString += `<h2 style="text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px;">${jsonPayload.document_type || "DISCHARGE SUMMARY"}</h2>`;

  // 1. Header & Hospital Details
  if (jsonPayload.header) {
    const h = jsonPayload.header;
    htmlString += `<table style="width: 100%; margin-top: 10px; margin-bottom: 20px; border-collapse: collapse;">
      <tr>
        <td style="width: 50%;"><strong>Patient Name:</strong> ${safeStr(h.name)}</td>
        <td style="width: 50%;"><strong>Patient ID:</strong> ${safeStr(h.patient_id)}</td>
      </tr>
      <tr>
        <td><strong>Age/Gender:</strong> ${safeStr(h.age)} / ${safeStr(h.gender)}</td>
        <td><strong>Admission Date:</strong> ${formatClinicalDate(h.date_of_admission)}</td>
      </tr>
      <tr>
        <td><strong>Bed No:</strong> ${safeStr(h.bed_no)}</td>
        <td><strong>Discharge Date:</strong> ${formatClinicalDate(h.date_of_discharge)}</td>
      </tr>
      <tr>
        <td><strong>Category:</strong> ${safeStr(h.category)}</td>
        <td><strong>Consultant:</strong> ${safeStr(h.consultant_incharge)}</td>
      </tr>
    </table>`;

    if (h.discharge_details) {
      const dd = h.discharge_details;
      htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">Discharge Details</h3>`;
      htmlString += `<p><strong>Condition at Discharge:</strong> ${safeStr(dd.condition_at_discharge)}</p>`;
      htmlString += `<p><strong>Mobility Status:</strong> ${safeStr(dd.mobility_status)}</p>`;
      htmlString += `<p><strong>Follow-up Required:</strong> ${safeStr(dd.follow_up_required)}</p>`;
      htmlString += `<p><strong>Remarks:</strong> ${safeStr(dd.remarks)}</p>`;
      
      if (dd.vital_signs) {
        htmlString += `<p><strong>Vital Signs:</strong> BP: ${safeStr(dd.vital_signs.bp)}, Pulse: ${safeStr(dd.vital_signs.pulse)}, Temp: ${safeStr(dd.vital_signs.temperature)}, SpO2: ${safeStr(dd.vital_signs.spo2)}</p>`;
      }
    }
  }

  // 2. Final Diagnosis
  if (jsonPayload.final_diagnosis) {
    htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">Final Diagnosis</h3>`;
    const diags = safeArray(jsonPayload.final_diagnosis);
    if (diags.length) {
      htmlString += `<ul>${diags.map(d => `<li>${itemText(d)}</li>`).join("")}</ul>`;
    } else {
      htmlString += `<p><em>None reported</em></p>`;
    }
  }

  // 3. Investigations
  if (jsonPayload.investigations) {
    htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">Investigations</h3>`;
    const invs = safeArray(jsonPayload.investigations);
    if (invs.length) {
      htmlString += `<ul>${invs.map(i => `<li>${itemText(i)}</li>`).join("")}</ul>`;
    } else {
      htmlString += `<p><em>None reported</em></p>`;
    }
  }

  // 4. History
  if (jsonPayload.history) {
    const hist = jsonPayload.history;
    htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">History</h3>`;
    htmlString += `<p><strong>Presenting Illness:</strong> ${safeStr(hist.history_of_present_illness)}</p>`;
    
    const complaints = safeArray(hist.presenting_complaints);
    htmlString += `<p><strong>Presenting Complaints:</strong> ${complaints.length ? complaints.join(", ") : "-"}</p>`;
    
    htmlString += `<p><strong>Allergies:</strong> ${safeStr(hist.allergies)}</p>`;
    htmlString += `<p><strong>Past Medical History:</strong> ${safeStr(hist.past_medical_history)}</p>`;
    htmlString += `<p><strong>Past Surgical History:</strong> ${safeStr(hist.past_surgical_history)}</p>`;
    htmlString += `<p><strong>Family History:</strong> ${safeStr(hist.family_history)}</p>`;
    
    if (hist.personal_history) {
      const ph = hist.personal_history;
      htmlString += `<p><strong>Personal History:</strong> Diet: ${safeStr(ph.diet)}, Smoking: ${safeStr(ph.smoking)}, Alcohol: ${safeStr(ph.alcohol)}</p>`;
    }
  }

  // 5. Treatment Given
  if (jsonPayload.treatment_given) {
    const tg = jsonPayload.treatment_given;
    htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">Treatment Given</h3>`;
    
    htmlString += `<h4>Procedures</h4>`;
    const procs = safeArray(tg.procedures);
    if (procs.length) {
      htmlString += `<ul>${procs.map(p => `<li>${p}</li>`).join("")}</ul>`;
    } else {
      htmlString += `<p><em>None reported</em></p>`;
    }

    htmlString += `<h4>Medications Administered</h4>`;
    const meds = safeArray(tg.medications_administered).filter(m => m && (m.name || m.drug_name || m.dose || m.start || m.period));
    if (meds.length) {
      htmlString += `<table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
        <tr style="background-color: #f2f2f2;">
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Name</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Dose</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Frequency</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Start Date</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Period</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Unit</th>
        </tr>`;
      meds.forEach(m => {
        htmlString += `<tr>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.name || m.drug_name)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.dose)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.frequency)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${formatClinicalDate(m.start)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.period)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.periodUnit)}</td>
        </tr>`;
      });
      htmlString += `</table>`;
    } else {
      htmlString += `<p><em>None reported</em></p>`;
    }

    htmlString += `<h4>Supportive Care</h4>`;
    const care = safeArray(tg.supportive_care);
    if (care.length) {
      htmlString += `<ul>${care.map(c => `<li>${c}</li>`).join("")}</ul>`;
    } else {
      htmlString += `<p><em>None reported</em></p>`;
    }
  }

  // 6. Advices
  if (jsonPayload.advices) {
    const adv = jsonPayload.advices;
    htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">Discharge Advices</h3>`;
    
    htmlString += `<h4>Medications To Continue</h4>`;
    const meds = safeArray(adv.medications_to_continue).filter(m => m && (m.name || m.drug_name || m.dose || m.start || m.period));
    if (meds.length) {
      htmlString += `<table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
        <tr style="background-color: #f2f2f2;">
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Name</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Dose</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Frequency</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Start Date</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Period</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Unit</th>
        </tr>`;
      meds.forEach(m => {
        htmlString += `<tr>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.name || m.drug_name)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.dose)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.frequency)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${formatClinicalDate(m.start)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.period)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.periodUnit)}</td>
        </tr>`;
      });
      htmlString += `</table>`;
    } else {
      htmlString += `<p><em>None reported</em></p>`;
    }

    if (adv.diet) {
      const diets = safeArray(adv.diet.recommendations);
      htmlString += `<p><strong>Diet:</strong> ${safeStr(adv.diet.type)} ${diets.length > 0 ? "- " + diets.join(", ") : ""}</p>`;
    }

    const activity = safeArray(adv.activity_restrictions);
    if (activity.length) htmlString += `<p><strong>Activity Restrictions:</strong> ${activity.join(", ")}</p>`;

    const warnings = safeArray(adv.warning_signs);
    if (warnings.length) htmlString += `<p><strong>Warning Signs:</strong> ${warnings.join(", ")}</p>`;

    if (adv.follow_up) {
      htmlString += `<p><strong>Follow-up:</strong> ${safeStr(adv.follow_up.department)} in ${safeStr(adv.follow_up.after_days)} (Purpose: ${safeStr(adv.follow_up.purpose)})</p>`;
    }
  }

  // 7. Referral Details
  if (jsonPayload.referral_details) {
    const ref = jsonPayload.referral_details;
    htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">Referral Details</h3>`;
    htmlString += `<p><strong>Referred By:</strong> ${safeStr(ref.referred_by)} &nbsp; | &nbsp; <strong>Date:</strong> ${formatClinicalDate(ref.referral_date)}</p>`;
    htmlString += `<p><strong>Reason:</strong> ${safeStr(ref.reason_for_referral)}</p>`;
  }

  // Signatures
  htmlString += `<br/><br/>
    <div style="display: flex; justify-content: space-between; margin-top: 40px; margin-bottom: 20px;">
      <div>
        <p><strong>Prepared By:</strong> ${safeStr(jsonPayload.prepared_by?.name || "_________________")}</p>
        <p><small>${safeStr(jsonPayload.prepared_by?.designation)}</small></p>
      </div>
      <div>
        <p><strong>Checked By:</strong> ${safeStr(jsonPayload.checked_by?.name || "_________________")}</p>
        <p><small>${safeStr(jsonPayload.checked_by?.designation)}</small></p>
      </div>
    </div>
  </div>`;

  return htmlString;
};
